import React, { cloneElement, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconX } from '@tabler/icons-react';
import { ArtEditorContext } from '../../context';
// Relative: Jest resolves no webpack alias but the mocked `@fragments`.
import { useTopModalZIndex } from '../../../../../../../hooks/useModalZIndex';
import { enter } from '../../motion';
import './styles.scss';

// A floating panel with free content (color, font, spacing, export) anchored to its
// trigger. The `Popper` fragment only lists options, so the editor's pickers use this:
// same surface recipe (surface-10, hairline, overlay shadow, 16px corners), portaled to
// <body>, closed by Escape, a click elsewhere or `close()` passed to the content.
// With `dock`, inside an editor that has its settings dock (beside the art, so nothing
// covers it), the content opens there instead: one at a time, the trigger toggling it.
const GAP = 8;
const MARGIN = 8;

function place(anchor, panel, placement) {
  const a = anchor.getBoundingClientRect();
  const p = panel.getBoundingClientRect();
  const [side, align = 'center'] = placement.split('-');
  let top;
  let left;
  if (side === 'top' || side === 'bottom') {
    top = side === 'top' ? a.top - p.height - GAP : a.bottom + GAP;
    if (side === 'top' && top < MARGIN) top = a.bottom + GAP;
    if (side === 'bottom' && top + p.height > window.innerHeight - MARGIN && a.top - p.height - GAP > MARGIN) top = a.top - p.height - GAP;
    left = align === 'start' ? a.left : align === 'end' ? a.right - p.width : a.left + (a.width - p.width) / 2;
  } else {
    left = side === 'left' ? a.left - p.width - GAP : a.right + GAP;
    if (side === 'left' && left < MARGIN) left = a.right + GAP;
    if (side === 'right' && left + p.width > window.innerWidth - MARGIN) left = a.left - p.width - GAP;
    top = align === 'start' ? a.top : align === 'end' ? a.bottom - p.height : a.top + (a.height - p.height) / 2;
  }
  return {
    left: Math.max(MARGIN, Math.min(left, window.innerWidth - p.width - MARGIN)),
    top: Math.max(MARGIN, Math.min(top, window.innerHeight - p.height - MARGIN)),
    from: side === 'top' ? { y: 4 } : side === 'bottom' ? { y: -4 } : side === 'left' ? { x: 4 } : { x: -4 },
  };
}

export default function Popover({ trigger, children, placement = 'top', width = 280, className = '', onOpenChange, disabled = false, label, dock = false }) {
  const { t } = useTranslation('artEditor');
  const id = useId();
  const editorDock = useContext(ArtEditorContext)?.dock;
  const docked = dock && editorDock?.node ? editorDock : null;
  const [floating, setFloating] = useState(false);
  const open = docked ? docked.active === id : floating;
  const setOpen = useCallback(next => {
    if (docked) { const value = typeof next === 'function' ? next(docked.active === id) : next; if (value) docked.open(id); else docked.close(id); return; }
    setFloating(next);
  }, [docked, id]);
  // A docked panel whose trigger goes away (another element selected) leaves the dock.
  const closeDocked = useRef(null);
  closeDocked.current = docked ? () => docked.close(id) : null;
  useEffect(() => () => closeDocked.current?.(), []);
  const [position, setPosition] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const zIndex = useTopModalZIndex();
  const close = useCallback(() => setOpen(false), [setOpen]);
  useEffect(() => { onOpenChange?.(open); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (docked || !open || !anchorRef.current || !panelRef.current) { setPosition(null); return undefined; }
    const update = () => anchorRef.current && panelRef.current && setPosition(place(anchorRef.current, panelRef.current, placement));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const observer = new ResizeObserver(update);
    observer.observe(panelRef.current);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); observer.disconnect(); };
  }, [open, placement, docked]);

  const entered = useRef(false);
  useLayoutEffect(() => {
    if (!open) { entered.current = false; return; }
    if (docked && !entered.current) { entered.current = true; enter(panelRef.current, { x: 10 }, { duration: 0.18 }); return; }
    if (position && !entered.current) { entered.current = true; enter(panelRef.current, { ...position.from, scale: 0.98 }, { duration: 0.16 }); }
  }, [open, position, docked]);

  useEffect(() => {
    if (!open || docked) return undefined;
    const onPointer = event => {
      if (panelRef.current?.contains(event.target) || anchorRef.current?.contains(event.target)) return;
      // A menu or nested popover portaled from inside this one counts as inside.
      if (event.target.closest?.('.custom-popper-portal, .art-popover, .MuiPopover-root, .MuiTooltip-popper')) return;
      setOpen(false);
    };
    const onKey = event => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); anchorRef.current?.focus?.(); } };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('pointerdown', onPointer, true); document.removeEventListener('keydown', onKey, true); };
  }, [open, docked, setOpen]);

  const anchor = cloneElement(trigger, {
    ref: anchorRef,
    'aria-expanded': open,
    'aria-haspopup': 'dialog',
    'aria-pressed': docked ? open : undefined,
    onClick: event => { trigger.props.onClick?.(event); if (!disabled) setOpen(value => !value); },
  });
  const content = typeof children === 'function' ? children({ close }) : children;
  if (docked) {
    return <>
      {anchor}
      {open && createPortal(<section ref={panelRef} className={`art-dock__panel ${className}`} aria-label={label}
        onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); close(); anchorRef.current?.focus?.(); } }}>
        <header className="art-dock__header">
          <Text.label>{label}</Text.label>
          <Button size="small" variant="text" color="overlay" preventLoading icon={<IconX />} aria-label={t('closePanel')} tooltip={t('closePanel')} onClick={close} />
        </header>
        <div className="art-dock__body">{content}</div>
      </section>, docked.node)}
    </>;
  }
  return <>
    {anchor}
    {open && createPortal(<div ref={panelRef} role="dialog" aria-label={label} className={`art-popover ${className}`}
      style={{ width, zIndex, left: position?.left ?? -9999, top: position?.top ?? -9999, visibility: position ? 'visible' : 'hidden' }}>
      {content}
    </div>, document.body)}
  </>;
}
